import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../common/Icon';
import AgGrid from '../common/AgGrid';
import * as XLSX from 'xlsx';
import ReactDOM from 'react-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import { GetBackendCost, DeletePackageCosts, CreateBackendCost, UpdateBackendCost, CostTypePackageCost } from '../../services/accountService';
import SuccessMessage from '../common/SuccessMessage';

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return Number(value).toLocaleString();
};

const EditableInputRenderer = memo(({
  value,
  id,
  field,
  type = 'text',
  isCurrency = false,
  placeholder = '',
  isEditMode,
  onFieldChange
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  if (!isEditMode) {
    if (isCurrency) {
      const displayValue = (value === null || value === undefined) ? 0 : value;
      return <span>${Number(displayValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
    }
    if (type === 'number') {
      const displayValue = (value === null || value === undefined) ? 0 : value;
      return <span>{Number(displayValue).toLocaleString()}</span>;
    }
    return <span>{value || ''}</span>;
  }

  const isRequiredField = field === 'backendCostType';
  const isInvalid = isRequiredField && (!localValue || String(localValue).trim() === '');
  const isNewRow = id && String(id).startsWith('temp-');

  return (
    <input
      type={type}
      value={localValue ?? ''}
      placeholder={placeholder || (isRequiredField ? 'Required *' : '')}
      onChange={(e) => {
        let newValue = e.target.value;
        if (type === 'number') {
          newValue = newValue === '' ? '' : (parseFloat(newValue) || 0);
        }
        setLocalValue(newValue);
      }}
      onBlur={() => {
        let finalValue = localValue;
        if (type === 'number') {
          finalValue = (localValue === '' || localValue === null || localValue === undefined)
            ? 0
            : (parseFloat(localValue) || 0);
        }

        if (String(finalValue) !== String(value ?? '')) {
          onFieldChange(id, field, finalValue);
        }
      }}

      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        padding: '6px 8px',
        border: `1px solid ${isInvalid && isNewRow ? '#dc3545' : '#007bff'}`,
        borderRadius: '4px',
        fontSize: '13px',
        fontFamily: 'inherit',
        textAlign: isCurrency ? 'right' : 'left',
        backgroundColor: isInvalid && isNewRow ? '#fff3f3' : 'white',
        outline: 'none'
      }}
      step={type === 'number' ? "0.01" : undefined}
    />
  );
});
EditableInputRenderer.displayName = 'EditableInputRenderer';

const MFRateDropdown = memo(({ value, id, field, isEditMode, onFieldChange }) => {
  let currentValue = 'F';
  if (value === true || value === 'M') {
    currentValue = 'M';
  } else if (value === false || value === 'F') {
    currentValue = 'F';
  }

  if (!isEditMode) {

    return (
      <select
        value={currentValue}
        disabled={true}
        onChange={() => { }}
        style={{
          width: '80px',
          padding: '6px 10px',
          border: '1px solid #d0d0d0',
          borderRadius: '4px',
          backgroundColor: '#FFFFFF',
          cursor: 'default',
          fontSize: '13px',
          fontFamily: 'inherit',
          textAlign: 'center',
          fontWeight: 'normal',
          outline: 'none',
          opacity: 0.8
        }}
      >
        <option value="M">M</option>
        <option value="F">F</option>
      </select>
    );
  }

  return (
    <select
      value={currentValue}
      onChange={(e) => {
        onFieldChange(id, field, e.target.value);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '80px',
        padding: '6px 8px',
        border: '1px solid #007bff',
        borderRadius: '4px',
        fontSize: '13px',
        fontFamily: 'inherit',
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: 'white'
      }}
    >
      <option value="M">M</option>
      <option value="F">F</option>
    </select>
  );
});
MFRateDropdown.displayName = 'MFRateDropdown';

const StatusDropdownCell = memo(({ data, isEditMode, onFieldChange }) => {
  const currentValue = data?.status || 'No';
  const id = data?.id;

  if (!id || data?.isTotalsRow || data?.isAddRow) {
    return <span></span>;
  }

  return (
    <select
      value={currentValue}
      onChange={(e) => {
        e.stopPropagation();
        const isFinal = e.target.value === 'Yes';
        onFieldChange(id, 'isCostFinal', isFinal);
        onFieldChange(id, 'status', e.target.value);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      disabled={!isEditMode}
      style={{
        width: '80px',
        padding: '6px 10px',
        border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
        borderRadius: '4px',
        backgroundColor: isEditMode ? '#ffffff' : '#FFFFFF',
        cursor: isEditMode ? 'pointer' : 'default',
        fontSize: '13px',
        fontFamily: 'inherit',
        fontWeight: '500',
        textAlign: 'center',
        outline: 'none',
        opacity: isEditMode ? 1 : 0.8,
        color: isEditMode ? '#000' : '#6c757d'
      }}
    >
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  );
});
StatusDropdownCell.displayName = 'StatusDropdownCell';


const CurrencyRenderer = (params) => {
  if (params.data?.isTotalsRow) {
    return <span style={{ fontWeight: 'bold' }}>${params.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>;
  }
  const value = params.value || 0;
  return <span>${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
};

const VarianceRenderer = (params) => {
  const variance = params.value;
  if (variance === null || variance === undefined) return <span></span>;

  const symbol = variance < 0 ? '-' : '';
  const color = '#333';
  const formatted = `${symbol}$${Math.abs(variance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (params.data?.isTotalsRow) {
    return <span style={{ fontWeight: 'bold', color }}>{formatted}</span>;
  }
  return <span style={{ color }}>{formatted}</span>;
};

const VariancePercentRenderer = (params) => {
  const variancePercent = params.value;
  if (variancePercent === null || variancePercent === undefined) return <span></span>;

  const symbol = variancePercent < 0 ? '-' : '';
  const color = '#333';
  const formatted = `${symbol}${Math.abs(variancePercent).toFixed(2)}%`;

  if (params.data?.isTotalsRow) {
    return <span style={{ fontWeight: 'bold', color }}>{formatted}</span>;
  }
  return <span style={{ color }}>{formatted}</span>;
};


const formatCurrencyValue = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(value)) return '$0.00';
  const n = parseFloat(value) || 0;
  const abs = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `-$${abs}` : `$${abs}`;
};

const formatPercentValue = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(value)) return '0.00%';
  const n = parseFloat(value) || 0;
  const abs = Math.abs(n).toFixed(2);
  return n < 0 ? `-${abs}%` : `${abs}%`;
};

const BackendCostsDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [backendData, setBackendData] = useState([]);
  const [originalBackendData, setOriginalBackendData] = useState([]);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [showCostTypeDropdown, setShowCostTypeDropdown] = useState(false);
  const [selectedCostType, setSelectedCostType] = useState('backend');
  const [gridApi, setGridApi] = useState(null);


  const [filteredBackendData, setFilteredBackendData] = useState(null);
  const gridApiRef = useRef(null);

  const agGridRef = useRef(null);

  const menuDropdownRef = useRef(null);

  // Delete flow
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCostsToDelete, setSelectedCostsToDelete] = useState([]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [costTypeOptions, setCostTypeOptions] = useState([]);

  const clientId = location.state?.clientId;

  const [navigationSource, setNavigationSource] = useState(() => {
    if (location.state?.source) return location.state.source;
    const stored = sessionStorage.getItem('jobDetailsSource');
    return stored || 'job-module';
  });

  const [isAllClientsFlow, setIsAllClientsFlow] = useState(() => {
    if (location.state?.isAllClients !== undefined) {
      return Boolean(location.state.isAllClients);
    }
    return sessionStorage.getItem('isAllClientsFlow') === 'true';
  });


  const [cameFromIntegration, setCameFromIntegration] = useState(() => {
    if (location.state?.fromIntegration !== undefined) {
      return Boolean(location.state.fromIntegration);
    }
    return sessionStorage.getItem('cameFromIntegration') === 'true';
  });

  const [reportsContext, setReportsContext] = useState(() => {
    if (location.state?.fromReports) {
      return {
        fromReports: true,
        reportFilters: location.state.reportFilters || {},
        selectedClientId: location.state.selectedClientId || [],
        reportType: location.state.reportType || '',
        rowData: location.state.rowData || []
      };
    }
    const stored = sessionStorage.getItem('reportsContext');
    return stored ? JSON.parse(stored) : null;
  });

  const showReportsCrumb =

    reportsContext?.fromReports &&

    navigationSource === 'reports';

  useEffect(() => {
    if (location.state?.fromReports) {
      const ctx = {
        fromReports: true,
        reportFilters: location.state.reportFilters || {},
        selectedClientId: location.state.selectedClientId || [],
        reportType: location.state.reportType || '',
        rowData: location.state.rowData || []
      };
      sessionStorage.setItem('reportsContext', JSON.stringify(ctx));
      setReportsContext(ctx);
    }
  }, [location.state]);


  useEffect(() => {
    if (location.state?.source) {
      sessionStorage.setItem('jobDetailsSource', location.state.source);
      setNavigationSource(location.state.source);
    }
    if (location.state?.isAllClients !== undefined) {
      sessionStorage.setItem(
        'isAllClientsFlow',
        String(Boolean(location.state.isAllClients))
      );
      setIsAllClientsFlow(Boolean(location.state.isAllClients));
    }
  }, [location.state]);

  const showClientSelectCrumb =
    navigationSource === 'dashboard' ||
    navigationSource === 'client-dashboard' ||
    isAllClientsFlow;

  const showClientNameCrumb =
    (navigationSource === 'dashboard' ||
      navigationSource === 'client-dashboard') &&
    !isAllClientsFlow;


  const activeJobId = jobId || location.state?.jobId;
  const jobCode = location.state?.jobCode;
  const clientName = location.state?.clientName;

  const [successMessage, setSuccessMessage] = useState('');

  const [messageType, setMessageType] = useState('success');

  const [displayTotals, setDisplayTotals] = useState({
    clientEstimate: 0,
    totalClientEstCost: 0,
    totalEstCost: 0,
    totalActualCost: 0,
    totalVariance: 0,
    totalVariancePercent: 0,
    totalEstQuantity: 0,
    totalActualQuantity: 0,
    totalActualCostDisplay: 0
  });


  // ✅ ADD THIS COMPONENT (reuse from Package file)
  const CostTypeDropdown = memo(({
    value,
    id,
    costTypeOptions,
    isEditMode,
    onFieldChange
  }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, openUp: false, maxHeight: 250 });
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
      setSearchTerm(value || '');
    }, [value]);

    const updateDropdownPosition = useCallback(() => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      const PANEL_HEIGHT = 250;
      const MARGIN = 8;

      const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
      const spaceAbove = rect.top - MARGIN;


      const openUp = spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow;

      setDropdownPos({
        top: openUp ? rect.top - 2 : rect.bottom + 2,
        left: rect.left,
        width: rect.width,
        openUp,
        maxHeight: Math.max(120, Math.min(PANEL_HEIGHT, openUp ? spaceAbove : spaceBelow))
      });
    }, []);
    useEffect(() => {
      if (!showDropdown) return;
      const handleClickOutside = (e) => {
        const clickedInWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
        const clickedInPanel = panelRef.current && panelRef.current.contains(e.target);
        if (!clickedInWrapper && !clickedInPanel) setShowDropdown(false);
      };
      const handleReposition = () => updateDropdownPosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleReposition, true);
      window.addEventListener('resize', handleReposition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleReposition, true);
        window.removeEventListener('resize', handleReposition);
      };
    }, [showDropdown, updateDropdownPosition]);

    if (!isEditMode) return <span>{value || ''}</span>;

    const filteredOptions = (costTypeOptions || []).filter(opt =>
      opt.costTypeName.toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    console.log("filteredOptions", filteredOptions)
    const openDropdown = () => { updateDropdownPosition(); setShowDropdown(true); };

    const handleSelectOption = (option) => {
      onFieldChange(id, 'backendCostType', option.costTypeName);
      onFieldChange(id, 'costTypeID', option.costTypeID);
      onFieldChange(id, 'costCategoryID', option.costCategoryID);
      setSearchTerm(option.costTypeName);
      setShowDropdown(false);
    };

    const handleInputChange = (e) => {
      const newValue = e.target.value;
      setSearchTerm(newValue);
      if (!showDropdown) openDropdown();
      onFieldChange(id, 'backendCostType', newValue);
      onFieldChange(id, 'costTypeID', null);
    };

    const handleInputBlur = () => {
      const match = (costTypeOptions || []).find(
        opt => opt.costTypeName.toLowerCase() === (searchTerm || '').toLowerCase()
      );
      if (match) {
        onFieldChange(id, 'backendCostType', match.costTypeName);
        onFieldChange(id, 'costTypeID', match.costTypeID);
        onFieldChange(id, 'costCategoryID', match.costCategoryID);
      }
    };

    const isInvalid = !searchTerm || searchTerm.trim() === '';

    const dropdownPanel = showDropdown ? ReactDOM.createPortal(
      <div
        ref={panelRef}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: dropdownPos.top, left: dropdownPos.left,
          width: dropdownPos.width, minWidth: '200px', backgroundColor: '#fff',
          border: '1px solid #ccc', borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 99999,
          maxHeight: dropdownPos.maxHeight, overflowY: 'auto',
          transform: dropdownPos.openUp ? 'translateY(-100%)' : 'none'
        }}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <div
              key={option.costTypeID}
              onMouseDown={(e) => { e.preventDefault(); handleSelectOption(option); }}
              style={{
                padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                backgroundColor: searchTerm === option.costTypeName ? '#e6f7ff' : '#fff',
                fontSize: '13px', transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => { if (searchTerm !== option.costTypeName) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
              onMouseLeave={(e) => { if (searchTerm !== option.costTypeName) e.currentTarget.style.backgroundColor = '#fff'; }}
            >
              {option.costTypeName}
            </div>
          ))
        ) : (
          <div style={{ padding: '10px 12px', color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
            {searchTerm ? `No match — "${searchTerm}" will be added as new` : 'No cost types available'}
          </div>
        )}
      </div>,
      document.body
    ) : null;

    return (
      <div
        ref={wrapperRef}
        style={{ position: 'relative', width: '100%' }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          placeholder="Select or Add *"
          onChange={handleInputChange}
          onFocus={openDropdown}
          onClick={openDropdown}
          onBlur={handleInputBlur}
          style={{
            width: '100%', padding: '6px 8px',
            border: `1px solid ${isInvalid ? '#dc3545' : '#007bff'}`,
            borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit',
            backgroundColor: isInvalid ? '#fff3f3' : 'white',
            outline: 'none', boxSizing: 'border-box', cursor: 'text'
          }}
        />
        {dropdownPanel}
      </div>
    );
  });
  CostTypeDropdown.displayName = 'CostTypeDropdown';

  // Transform API response to match component data structure
  const transformBackendData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      return [];
    }

    return apiData.map(item => ({
      id: item.jobCostID,
      jobCostID: item.jobCostID,
      jobID: item.jobID,
      costCategoryID: item.costCategoryID,
      costTypeID: item.costTypeID,
      backendCostType: item.costTypeName || item.displayName || '',
      description: item.costComponentDescription || '',
      clientEstimateQuantity: item.preInvoiceNumber || '',
      clientEstimate: item.preInvoiceCost || 0,
      clientRateType: item.isPreInvoiceCostPerThousand === true ? 'M' : 'F',
      isPreInvoiceCostPerThousand: item.isPreInvoiceCostPerThousand || false,
      totalClientEstCost: item.totalClientEstCost || 0,
      poNumber: item.poNumber || '',
      vendor: item.vendor || '',
      invoiceNumber: item.invoiceNumber || '',
      estQuantity: item.estimatedQuantity || 0,
      estCost: item.estimatedCost || 0,
      estimatedRateType: item.isEstimatedCostPerThousand === true ? 'M' : 'F',
      isEstimatedCostPerThousand: item.isEstimatedCostPerThousand || false,
      totalEstCost: item.totalEstCost || 0,
      actualQuantity: item.actualQuantity || 0,
      actualCost: item.actualCost || 0,
      actualRateType: item.isActualCostPerThousand === true ? 'M' : 'F',
      isActualCostPerThousand: item.isActualCostPerThousand || false,
      totalActualCost: item.totalActualCost || 0,
      isCostFinal: item.isCostFinal || false,
      status: item.isCostFinal ? 'Yes' : 'No',
      actulaCostMFCalc: item.actulaCostMFCalc || 0,
      costVariance: item.costVariance || 0,
      costVariancePercentage: item.costVariancePercentage || 0,
      costPerThousand: item.costPerThousand || 0,
      totalEstimatedCost: item.totalEstCost || 0,
      totalClientCost: item.totalClientEstCost || 0
    }));
  };


  const transformToCreatePayload = (frontendData) => {
    return frontendData.map(item => ({
      jobID: Number(activeJobId),
      costCategoryID: item.costCategoryID || 3,
      costTypeID: item.costTypeID || null,
      costComponentName: item.backendCostType || '',
      costComponentDescription: item.description || '',
      estimatedCost: Number(item.estCost) || 0,
      estimatedQuantity: Number(item.estQuantity) || 0,
      isEstimatedCostPerThousand: item.estimatedRateType === 'M',
      poNumber: item.poNumber || '',
      vendor: item.vendor || '',
      status: true,
      actualCost: Number(item.actualCost) || 0,
      actualQuantity: Number(item.actualQuantity) || 0,
      isActualCostPerThousand: item.actualRateType === 'M',
      invoiceNumber: item.invoiceNumber || '',
      preInvoiceCost: Number(item.clientEstimate) || 0,
      isPreInvoiceCostPerThousand: item.clientRateType === 'M',
      preInvoiceNumber: item.clientEstimateQuantity?.toString() || '',
      actulaCostMFCalc: item.actulaCostMFCalc || 0,
      isCostFinal: item.isCostFinal || false
    }));
  };


  const calculateTotalsFromFilteredData = useCallback((filteredData) => {
    if (!filteredData || filteredData.length === 0) {
      return {
        clientEstimate: 0,
        totalClientEstCost: 0,
        totalEstCost: 0,
        totalActualCost: 0,
        totalVariance: 0,
        totalVariancePercent: 0,
        totalEstQuantity: 0,
        totalActualQuantity: 0,
        totalActualCostDisplay: 0
      };
    }

    let clientEstimate = 0;
    let totalClientEstCost = 0;
    let totalEstCost = 0;
    let totalActualCost = 0;
    let totalEstQuantity = 0;
    let totalActualQuantity = 0;

    filteredData.forEach(row => {
      if (row.isTotalsRow || row.isAddRow) return;
      clientEstimate += parseFloat(row.clientEstimate) || 0;
      totalClientEstCost += parseFloat(row.totalClientEstCost) || 0;
      totalEstCost += parseFloat(row.totalEstCost) || 0;
      totalActualCost += parseFloat(row.totalActualCost) || 0;
      totalEstQuantity = Math.max(totalEstQuantity, parseFloat(row.estQuantity) || 0);
      totalActualQuantity += parseFloat(row.actualQuantity) || 0;
    });

    const totalVariance = totalActualCost - totalClientEstCost;
    const totalVariancePercent =
      totalActualCost !== 0
        ? ((totalActualCost - totalClientEstCost) * 100) / totalActualCost
        : 0;

    return {
      clientEstimate: Math.round(clientEstimate * 100) / 100,
      totalClientEstCost: Math.round(totalClientEstCost * 100) / 100,
      totalEstCost: Math.round(totalEstCost * 100) / 100,
      totalActualCost: Math.round(totalActualCost * 100) / 100,
      totalVariance: Math.round(totalVariance * 100) / 100,
      totalVariancePercent: Math.round(totalVariancePercent * 100) / 100,
      totalEstQuantity: Math.round(totalEstQuantity * 100) / 100,
      totalActualQuantity: Math.round(totalActualQuantity * 100) / 100,
      totalActualCostDisplay: Math.round(totalActualCost * 100) / 100
    };
  }, []);

  const calculateTotalsFromData = useCallback((data) => {
    if (!data || data.length === 0) {
      return {
        clientEstimate: 0,
        totalClientEstCost: 0,
        totalEstCost: 0,
        totalActualCost: 0,
        totalVariance: 0,
        totalVariancePercent: 0,
        totalEstQuantity: 0,
        totalActualQuantity: 0,
        totalActualCostDisplay: 0
      };
    }

    let clientEstimate = 0;
    let totalClientEstCost = 0;
    let totalEstCost = 0;
    let totalActualCost = 0;
    let totalEstQuantity = 0;
    let totalActualQuantity = 0;

    data.forEach(row => {
      if (row.isTotalsRow || row.isAddRow) return;
      clientEstimate += parseFloat(row.clientEstimate) || 0;
      totalClientEstCost += parseFloat(row.totalClientEstCost) || 0;
      totalEstCost += parseFloat(row.totalEstCost) || 0;
      totalActualCost += parseFloat(row.totalActualCost) || 0;
      totalEstQuantity = Math.max(totalEstQuantity, parseFloat(row.estQuantity) || 0);
      totalActualQuantity = parseFloat(row.actualQuantity) || 0;
    });

    const totalVariance = totalActualCost - totalClientEstCost;
    const totalVariancePercent =
      totalActualCost !== 0
        ? ((totalActualCost - totalClientEstCost) * 100) / totalActualCost
        : 0;

    return {
      clientEstimate: Math.round(clientEstimate * 100) / 100,
      totalClientEstCost: Math.round(totalClientEstCost * 100) / 100,
      totalEstCost: Math.round(totalEstCost * 100) / 100,
      totalActualCost: Math.round(totalActualCost * 100) / 100,
      totalVariance: Math.round(totalVariance * 100) / 100,
      totalVariancePercent: Math.round(totalVariancePercent * 100) / 100,
      totalEstQuantity: Math.round(totalEstQuantity * 100) / 100,
      totalActualQuantity: Math.round(totalActualQuantity * 100) / 100,
      totalActualCostDisplay: Math.round(totalActualCost * 100) / 100
    };
  }, []);

  // ------------------------------------------------------------------
  // ✅ handleFilterChanged — now writes null when no filter is active
  // (instead of []), so a filter that matches nothing can never be
  // confused with "no filter applied at all." The actual totals
  // computation is left to the effect below, which is the single
  // source of truth for displayTotals.
  // ------------------------------------------------------------------
  const handleFilterChanged = useCallback((params) => {
    const api = params?.api || gridApiRef.current;
    if (!api || typeof api.forEachNodeAfterFilterAndSort !== 'function') return;

    const isFilterActive = typeof api.isAnyFilterPresent === 'function'
      ? api.isAnyFilterPresent()
      : false;

    if (!isFilterActive) {
      setFilteredBackendData(null);
      return;
    }

    const filteredRows = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data && !node.data.isTotalsRow && !node.data.isAddRow) {
        filteredRows.push(node.data);
      }
    });

    setFilteredBackendData(filteredRows);
  }, []);


  useEffect(() => {
    if (filteredBackendData !== null) {
      const newTotals = calculateTotalsFromFilteredData(filteredBackendData);
      setDisplayTotals(newTotals);
    } else if (backendData.length > 0) {
      const allData = backendData.filter(item => !item.isTotalsRow && !item.isAddRow);
      const newTotals = calculateTotalsFromData(allData);
      setDisplayTotals(newTotals);
    } else {
      setDisplayTotals({
        clientEstimate: 0,
        totalClientEstCost: 0,
        totalEstCost: 0,
        totalActualCost: 0,
        totalVariance: 0,
        totalVariancePercent: 0,
        totalEstQuantity: 0,
        totalActualQuantity: 0,
        totalActualCostDisplay: 0
      });
    }
  }, [backendData, filteredBackendData, calculateTotalsFromFilteredData, calculateTotalsFromData]);


  useEffect(() => {
    setFilteredBackendData(null);
  }, [backendData.length]);

  // Update totals function
  const updateTotals = useCallback((data) => {
    const newTotals = calculateTotalsFromData(data);
    setDisplayTotals(newTotals);
  }, [calculateTotalsFromData]);

  // Fetch latest backend costs from API
  const fetchLatestBackendCosts = async () => {
    if (!activeJobId) return [];

    try {
      setLoading(true);
      const response = await GetBackendCost(activeJobId);

      let data = [];
      if (response && Array.isArray(response)) {
        data = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (response && response.items && Array.isArray(response.items)) {
        data = response.items;
      } else {
        console.warn('Unexpected API response structure:', response);
        data = [];
      }

      return data;
    } catch (error) {
      console.error('Error fetching backend costs:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh data from API
  const refreshDataFromAPI = async () => {
    try {
      setLoading(true);
      const latestData = await fetchLatestBackendCosts();
      const transformedData = transformBackendData(latestData);
      setBackendData(transformedData);
      setOriginalBackendData(JSON.parse(JSON.stringify(transformedData)));
      updateTotals(transformedData);
      return transformedData;
    } catch (error) {
      console.error('Error refreshing data:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Initialize data
  const initializeBackendData = async () => {
    try {
      setLoading(true);
      const data = await fetchLatestBackendCosts();
      const transformedData = transformBackendData(data);
      setBackendData(transformedData);
      setOriginalBackendData(JSON.parse(JSON.stringify(transformedData)));
      updateTotals(transformedData);
      return transformedData;
    } catch (error) {
      console.error('Error initializing backend data:', error);
      setBackendData([]);
      setOriginalBackendData([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (activeJobId) {
      initializeBackendData();
    }
  }, [activeJobId]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
        setShowDropdownMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleFieldChange = useCallback((id, field, value) => {
    setBackendData(prevData => {
      const updatedData = prevData.map(item => {
        if (item.id === id) {
          let updatedItem = { ...item, [field]: value };

          if (field === 'actualRateType') {
            updatedItem.isActualCostPerThousand = value === 'M';
          }
          if (field === 'estimatedRateType') {
            updatedItem.isEstimatedCostPerThousand = value === 'M';
          }
          if (field === 'clientRateType') {
            updatedItem.isPreInvoiceCostPerThousand = value === 'M';
          }
          if (field === 'isActualCostPerThousand') {
            updatedItem.actualRateType = value ? 'M' : 'F';
          }
          if (field === 'isEstimatedCostPerThousand') {
            updatedItem.estimatedRateType = value ? 'M' : 'F';
          }
          if (field === 'isPreInvoiceCostPerThousand') {
            updatedItem.clientRateType = value ? 'M' : 'F';
          }

          return updatedItem;
        }
        return item;
      });
      return updatedData;
    });
  }, []);


  const resolveCostCategoryID = useCallback(() => {
    const rowWithCategory = backendData.find(r => r.costCategoryID && !r.isAddRow && !r.isTotalsRow);
    return rowWithCategory?.costCategoryID ?? 3;
  }, [backendData]);

  const resolveExistingActualQuantity = useCallback(() => {
    const rowWithQuantity = backendData.find(
      r => !r.isAddRow && !r.isTotalsRow && parseFloat(r.actualQuantity) > 0
    );
    return rowWithQuantity ? (parseFloat(rowWithQuantity.actualQuantity) || 0) : 0;
  }, [backendData]);

  const resolveExistingEstQuantity = useCallback(() => {
    const rowWithQuantity = backendData.find(
      r => !r.isAddRow && !r.isTotalsRow && parseFloat(r.estQuantity) > 0
    );
    return rowWithQuantity ? (parseFloat(rowWithQuantity.estQuantity) || 0) : 0;
  }, [backendData]);
  const handleAddNewRow = async () => {
    if (!gridApi) return;
    const categoryID = resolveCostCategoryID();
    const existingEstQuantity = resolveExistingEstQuantity();
    const existingActualQuantity = resolveExistingActualQuantity();
    gridApi.stopEditing();

    await fetchCostTypeOptions(categoryID);
    const tempId = `temp-${Date.now()}`;

    const newRow = {
      id: tempId,
      jobCostID: tempId,
      jobID: activeJobId,
      isNewRow: true,
      costCategoryID: categoryID,
      costTypeID: null,
      backendCostType: '',
      description: '',
      clientEstimateQuantity: '',
      clientEstimate: 0,
      isPreInvoiceCostPerThousand: false,
      totalClientCost: 0,
      totalClientEstCost: 0,
      poNumber: '',
      vendor: '',
      invoiceNumber: '',
      estQuantity: existingEstQuantity,
      estCost: 0,
      isEstimatedCostPerThousand: false,
      totalEstCost: 0,
      actualQuantity: existingActualQuantity,
      actualCost: 0,
      isActualCostPerThousand: false,
      totalActualCost: 0,
      isCostFinal: false,
      status: 'No',
      im: 0,
      costVariance: 0,
      costVariancePercentage: 0,
      actualRateType: 'F',
      estimatedRateType: 'F',
      clientRateType: 'F',
      estimatedCostPerThousand: 0,
      actualCostPerThousand: 0,
      clientCostPerThousand: 0,
      costPerThousand: 0,
      vendorQuantity: 0,
      vendorCost: 0,
      vendorCostVariance: 0,
      vendorCostVariancePercent: 0
    };

    setBackendData(prev => [...prev, newRow]);
  };;

  // Handle Cell Click
  const handleCellClicked = (params) => {
    if (params.data?.isAddRow && isEditMode) {
      handleAddNewRow();
    }
  };

  // Handle Delete click
  const handleDeleteClick = () => {
    if (!gridApi) return;

    const selectedRows = gridApi.getSelectedRows();

    if (!selectedRows || selectedRows.length === 0) {
      // alert('Please select components to delete');

      setSuccessMessage('Please select components to delete');
      setMessageType('error');

      return;
    }

    setSelectedCostsToDelete(selectedRows);
    setShowDeleteConfirm(true);
  };

  // Handle Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedCostsToDelete.length) return;

    try {
      setDeleteInProgress(true);

      const apiIds = [];
      const tempRows = [];

      selectedCostsToDelete.forEach(row => {
        if (row.jobCostID && !String(row.jobCostID).startsWith('temp-')) {
          apiIds.push(row.jobCostID);
        } else {
          tempRows.push(row);
        }
      });

      if (tempRows.length > 0) {
        setBackendData(prev =>
          prev.filter(row => !tempRows.includes(row))
        );
      }

      if (apiIds.length > 0) {
        await DeletePackageCosts(apiIds);
        await refreshDataFromAPI();
      }

      setShowDeleteConfirm(false);
      setSelectedCostsToDelete([]);
      setSuccessMessage('Deleted successfully!');

    } catch (error) {
      console.error('Delete failed:', error);
      // alert('Delete failed: ' + (error.message || 'Please try again'));

      setSuccessMessage('Delete failed: ' + (error.message || 'Please try again'));
      setMessageType('error');

    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedCostsToDelete([]);
  };


  const fetchCostTypeOptions = async (costCategoryID) => {
    try {
      const categoryID = Array.isArray(costCategoryID) ? costCategoryID[0] : costCategoryID;
      const payload = [Number(categoryID)];
      const response = await CostTypePackageCost(payload);
      let data = [];
      if (Array.isArray(response)) data = response;
      else if (response && Array.isArray(response.data)) data = response.data;
      else if (response && Array.isArray(response.items)) data = response.items;
      setCostTypeOptions(data);
      return data;
    } catch (error) {
      setCostTypeOptions([]);
      return [];
    }
  };

  const handleCostTypeChange = (type) => {
    setSelectedCostType(type);
    setShowCostTypeDropdown(false);

    switch (type) {
      case 'postage':
        navigate('/postage-details', {
          state: { jobId: activeJobId, jobCode, clientName }
        });
        break;
      case 'package':
        navigate('/package-costs-details', {
          state: { jobId: activeJobId, jobCode, clientName }
        });
        break;
      case 'backend':
        break;
      case 'lists':
        navigate('/lists-details', {
          state: { jobId: activeJobId, jobCode, clientName }
        });
        break;
      default:
        break;
    }
  };

  // Handle Save Changes
  const handleSaveChanges = async () => {
    if (gridApi) gridApi.stopEditing();

    try {
      setSavingChanges(true);

      const rows = [];
      gridApi.forEachNode(node => {
        if (!node.data?.isTotalsRow && !node.data?.isAddRow) {
          rows.push(node.data);
        }
      });

      const newRows = rows.filter(r =>
        !r.jobCostID || String(r.jobCostID).startsWith('temp-')
      );

      const existingRows = rows.filter(r =>
        r.jobCostID && !String(r.jobCostID).startsWith('temp-')
      );

      const invalidRows = newRows.filter(row => {
        const hasRequiredFields = row.backendCostType && row.backendCostType.trim() !== '';
        return !hasRequiredFields;
      });

      if (invalidRows.length > 0) {


        setSuccessMessage(`Please fill in the "Cost Type" field for ${invalidRows.length} new row(s) before saving`);
        setMessageType('error');

        setSavingChanges(false);
        return;
      }

      if (newRows.length > 0) {
        const createPayload = transformToCreatePayload(newRows);
        await CreateBackendCost(createPayload);
      }

      const changedRows = existingRows.filter(row => {
        const original = originalBackendData.find(o => o.id === row.id);
        if (!original) return false;

        const fieldsToCompare = [
          'backendCostType', 'description', 'clientEstimateQuantity', 'clientEstimate',
          'isPreInvoiceCostPerThousand', 'poNumber', 'vendor', 'invoiceNumber',
          'estQuantity', 'estCost', 'isEstimatedCostPerThousand',
          'actualQuantity', 'actualCost', 'isActualCostPerThousand',
          'status', 'isCostFinal',
          'clientRateType', 'estimatedRateType', 'actualRateType'
        ];

        return fieldsToCompare.some(field =>
          JSON.stringify(row[field]) !== JSON.stringify(original[field])
        );
      });

      if (changedRows.length > 0) {
        const updatePayload = changedRows.map(row => ({
          jobCostID: row.jobCostID,
          costCategoryID: row.costCategoryID || 3,
          costTypeID: row.costTypeID || null,
          costComponentName: row.backendCostType || '',
          costComponentDescription: row.description || '',
          estimatedCost: Number(row.estCost) || 0,
          estimatedQuantity: Number(row.estQuantity) || 0,
          isEstimatedCostPerThousand: row.estimatedRateType === 'M',
          poNumber: row.poNumber || '',
          vendor: row.vendor || '',
          status: row.status === 'Yes',
          actualCost: Number(row.actualCost) || 0,
          actualQuantity: Number(row.actualQuantity) || 0,
          isActualCostPerThousand: row.actualRateType === 'M',
          invoiceNumber: row.invoiceNumber || '',
          preInvoiceCost: Number(row.clientEstimate) || 0,
          isPreInvoiceCostPerThousand: row.clientRateType === 'M',
          preInvoiceNumber: row.clientEstimateQuantity?.toString() || '',
          actulaCostMFCalc: row.actulaCostMFCalc || 0,
          isCostFinal: row.isCostFinal || false
        }));

        await UpdateBackendCost(updatePayload, activeJobId);
      }

      await refreshDataFromAPI();
      setIsEditMode(false);
      setSuccessMessage('Changes saved successfully!');

    } catch (error) {
      console.error('Save failed:', error);
      // alert('Save failed: ' + (error.message || 'Please try again'));

      setSuccessMessage('Save failed: ' + (error.message || 'Please try again'));
      setMessageType('error');

    } finally {
      setSavingChanges(false);
    }
  };

  const handleCancelChanges = () => {
    setBackendData(JSON.parse(JSON.stringify(originalBackendData)));
    setIsEditMode(false);
  };

  const handleManageComponents = () => {
    setOriginalBackendData(JSON.parse(JSON.stringify(backendData)));
    setIsEditMode(true);
  };

  const handleJobDetailsClick = (e) => {
    e.preventDefault();
    navigate(`/job-details/${jobCode}`, {
      state: {
        jobId: activeJobId,
        jobCode,
        clientName,
        source: navigationSource,
        isAllClients: isAllClientsFlow,
        ...(cameFromIntegration ? { fromIntegration: true } : {}),
        ...(reportsContext?.fromReports
          ? {
            fromReports: true,
            reportFilters: reportsContext.reportFilters,
            selectedClientId: reportsContext.selectedClientId,
            reportType: reportsContext.reportType,
            rowData: reportsContext.rowData
          }
          : {})
      }
    });
  };

  const handleJobListsClick = (e) => {
    e.preventDefault();

    navigate('/job-module', {
      state: {
        clientId: clientId,
        source: navigationSource,
        isAllClients: isAllClientsFlow
      }
    });
  };



  const formatValueForField = (field, value) => {
    const currencyFields = [
      'clientEstimate', 'totalClientEstCost', 'totalClientCost', 'estCost',
      'totalEstCost', 'totalEstimatedCost', 'actualCost', 'totalActualCost', 'costVariance'
    ];
    const quantityFields = ['estQuantity', 'actualQuantity', 'costPerThousand', 'actulaCostMFCalc'];
    const rateTypeFields = ['clientRateType', 'estimatedRateType', 'actualRateType'];

    if (field === 'status') return value || '';
    if (field === 'isCostFinal') return value === true ? 'Yes' : 'No';

    if (field === 'costVariancePercentage') {
      if (value === null || value === undefined || value === '') return '';
      return formatPercentValue(value);
    }
    if (field === 'costVariance') {
      if (value === null || value === undefined || value === '') return '';
      return formatCurrencyValue(value);
    }
    if (currencyFields.includes(field)) {
      if (value === null || value === undefined || value === '') return '';
      return formatCurrencyValue(value);
    }
    if (quantityFields.includes(field)) {
      if (value === null || value === undefined || value === '') return '';
      const n = Number(value);
      return isNaN(n) ? String(value) : n.toLocaleString();
    }
    if (rateTypeFields.includes(field)) {
      return value === true || value === 'M' ? 'M' : 'F';
    }
    if (value === null || value === undefined) return '';
    return String(value);
  };


  const allExportColumns = useMemo(() => ([
    { field: 'backendCostType', headerName: 'Cost Type' },
    { field: 'description', headerName: 'Description' },
    { field: 'clientEstimateQuantity', headerName: 'Client Est. Inv #' },
    { field: 'clientEstimate', headerName: 'Client Estimate' },
    { field: 'clientRateType', headerName: 'M/F (Client)' },
    { field: 'totalClientEstCost', headerName: 'Total Client Est. Cost' },
    { field: 'poNumber', headerName: 'PO Number' },
    { field: 'vendor', headerName: 'Vendor' },
    { field: 'invoiceNumber', headerName: 'Invoice Number' },
    { field: 'estQuantity', headerName: 'Est. Quantity' },
    { field: 'estCost', headerName: 'Est. Cost' },
    { field: 'estimatedRateType', headerName: 'M/F (Est)' },
    { field: 'totalEstimatedCost', headerName: 'Total Est. Cost' },
    { field: 'actualQuantity', headerName: 'Actual Quantity' },
    { field: 'actualCost', headerName: 'Act. Cost' },
    { field: 'actualRateType', headerName: 'M/F (Act)' },
    { field: 'totalActualCost', headerName: 'Total Act. Cost' },
    { field: 'status', headerName: 'Status' },
    { field: 'actulaCostMFCalc', headerName: '/M' },
    { field: 'costVariance', headerName: 'Cost Variance' },
    { field: 'costVariancePercentage', headerName: 'Cost Variance %' }
  ]), []);


  const getExportColumns = useCallback(() => {
    if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
      const visible = agGridRef.current.getVisibleColumns();
      if (Array.isArray(visible)) {
        return visible.filter((c) => c.field && c.field !== 'action');
      }
    }
    return allExportColumns;
  }, [allExportColumns]);


  const getTotalForField = useCallback((field, totalsObj) => {
    switch (field) {
      case 'clientEstimate': return totalsObj.clientEstimate;
      case 'totalClientEstCost': return totalsObj.totalClientEstCost;
      case 'estQuantity': return totalsObj.totalEstQuantity;
      case 'totalEstCost':
      case 'totalEstimatedCost': return totalsObj.totalEstCost;
      case 'actualQuantity': return totalsObj.totalActualQuantity;
      case 'totalActualCost': return totalsObj.totalActualCostDisplay;
      case 'costVariance': return totalsObj.totalVariance;
      case 'costVariancePercentage': return totalsObj.totalVariancePercent;
      default: return undefined;
    }
  }, []);

  // ------------------------------------------------------------------
  // ✅ Helper: only real data rows from a given source (excludes the
  // add-row and totals placeholders). Reusable for either the full or
  // filtered scope.
  // ------------------------------------------------------------------
  const getExportableRowsFrom = useCallback((source) => {
    return (source || []).filter(item => !item.isAddRow && !item.isTotalsRow);
  }, []);


  const getExportRows = useCallback(() => {
    const allRows = getExportableRowsFrom(backendData);

    if (allRows.length === 0) {
      return { rows: [], scope: 'all' };
    }

    if (filteredBackendData !== null) {
      return { rows: getExportableRowsFrom(filteredBackendData), scope: 'filtered' };
    }

    return { rows: allRows, scope: 'all' };
  }, [backendData, filteredBackendData, getExportableRowsFrom]);


  const exportToExcel = () => {
    const { rows: validData } = getExportRows();

    // ✅ NO DATA
    if (!validData || validData.length === 0) {
      setSuccessMessage('No data to export');
      setMessageType('error');
      setShowDropdownMenu(false);
      return;
    }

    try {
      const exportColumns = getExportColumns();
      if (exportColumns.length === 0) {
        setSuccessMessage('No data to export');
        setMessageType('error');
        setShowDropdownMenu(false);
        return;
      }

      const exportData = validData.map((item) => {
        const row = {};
        exportColumns.forEach(({ field, headerName }) => {
          row[headerName] = formatValueForField(field, item[field]);
        });
        return row;
      });

      // ✅ TOTAL row at the bottom — recalculated from the exact rows exported
      const subtotals = calculateTotalsFromFilteredData(validData);
      const totalsRowOut = {};
      exportColumns.forEach(({ field, headerName }, idx) => {
        if (idx === 0) {
          totalsRowOut[headerName] = 'TOTAL';
        } else {
          const total = getTotalForField(field, subtotals);
          totalsRowOut[headerName] = total !== undefined
            ? formatValueForField(field, total)
            : '';
        }
      });
      exportData.push(totalsRowOut);

      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto column widths
      const colWidths = [];
      Object.keys(exportData[0] || {}).forEach((key, index) => {
        const maxLength = Math.max(
          key.length,
          ...exportData.map((row) => {
            const value = row[key];
            return value ? String(value).length : 0;
          })
        );
        colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Backend Costs');

      XLSX.writeFile(wb, `backend-costs.xlsx`);

      // ✅ SUCCESS
      setSuccessMessage('Export completed successfully!');
      setMessageType('success');
      setShowDropdownMenu(false);

    } catch (error) {
      console.error(error);

      setSuccessMessage('Error exporting data');
      setMessageType('error');
      setShowDropdownMenu(false);
    }
  };

  // ========== PRINT TO PDF (styled header card + KPI bar, respects visibility + filter scope) ==========
  const printToPDF = () => {
    const { rows: validData, scope } = getExportRows();

    if (!validData || validData.length === 0) {
      setSuccessMessage('No data to export');
      setMessageType('error');
      setShowDropdownMenu(false);
      return;
    }

    try {
      const exportColumns = getExportColumns();
      if (exportColumns.length === 0) {
        setSuccessMessage('No data to export');
        setMessageType('error');
        setShowDropdownMenu(false);
        return;
      }

      // ✅ Totals recalculated from the exact rows being exported
      const subtotals = calculateTotalsFromFilteredData(validData);

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 20;
      const cardX = marginX;
      const cardY = 20;
      const cardWidth = pageWidth - marginX * 2;


      const titleText =

        'BACKEND COSTS'
      const subtitleText = jobCode ? `Job · ${jobCode}` : 'BACKEND COSTS';

      const currentDate = new Date();
      const reportDateStr = `${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;


      const headerBarHeight = 60;
      const statsBarHeight = 50;
      const detailsBarHeight = 22;
      const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

      // Brand colors
      const BLUE_DARK = [30, 75, 120];
      const BLUE_MID = [46, 111, 159];
      const BLUE_DEEP = [22, 58, 95];


      doc.setFillColor(...BLUE_DARK);
      doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');


      const logoW = 90;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(180, 200, 220);
      doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

      // Title
      const titleAreaX = cardX + logoW;
      const titleAreaW = cardWidth - logoW;
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(titleText, titleAreaX + titleAreaW / 2, cardY + 28, { align: 'center' });

      // Subtitle
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(200, 215, 230);
      doc.text(subtitleText, titleAreaX + titleAreaW / 2, cardY + 44, { align: 'center' });

      // Report metadata (right side)
      const metaX = cardX + cardWidth - 12;
      doc.setFontSize(7);
      doc.setTextColor(220, 230, 240);
      doc.setFont('helvetica', 'normal');
      doc.text(`Report: ${reportDateStr}`, metaX, cardY + 16, { align: 'right' });

      const statsY = cardY + headerBarHeight;
      doc.setFillColor(...BLUE_MID);
      doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

      const stats = [
        { label: 'CLIENT EST. COST', value: formatCurrencyValue(subtotals.totalClientEstCost) },
        { label: 'TOTAL EST. COST', value: formatCurrencyValue(subtotals.totalEstCost) },
        { label: 'TOTAL ACT. COST', value: formatCurrencyValue(subtotals.totalActualCostDisplay) },
        { label: 'COST VARIANCE', value: formatCurrencyValue(subtotals.totalVariance) },
        { label: 'VARIANCE %', value: formatPercentValue(subtotals.totalVariancePercent) },
      ];

      const statCellW = cardWidth / stats.length;
      stats.forEach((stat, i) => {
        const cellX = cardX + i * statCellW;

        if (i > 0) {
          doc.setDrawColor(80, 130, 170);
          doc.setLineWidth(0.5);
          doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
        }

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(200, 220, 235);
        doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
      });


      const detailsY = statsY + statsBarHeight;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text('BACKEND DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

      const scopeLabel = scope === 'filtered' ? 'Filtered' : 'All';
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(200, 215, 230);
      doc.text(
        `${scopeLabel} Records: ${validData.length}  ·  Confidential`,
        cardX + cardWidth - 12,
        detailsY + detailsBarHeight / 2 + 3,
        { align: 'right' }
      );


      const tableStartY = cardY + cardHeight + 10;

      const head = [exportColumns.map((c) => c.headerName)];

      const body = validData.map((item) =>
        exportColumns.map(({ field }) => formatValueForField(field, item[field]))
      );

      const footerRow = exportColumns.map(({ field }, idx) => {
        if (idx === 0) return 'TOTAL';
        const total = getTotalForField(field, subtotals);
        return total !== undefined ? formatValueForField(field, total) : '';
      });

      const rightAlignedFields = [
        'clientEstimateQuantity', 'clientEstimate', 'totalClientEstCost', 'estQuantity',
        'estCost', 'totalEstCost', 'totalEstimatedCost', 'actualQuantity', 'actualCost',
        'totalActualCost', 'actulaCostMFCalc', 'costVariance', 'costVariancePercentage',
      ];
      const centerAlignedFields = ['clientRateType', 'estimatedRateType', 'actualRateType', 'status'];

      autoTable(doc, {
        head,
        body,
        foot: [footerRow],
        startY: tableStartY,
        margin: { left: marginX, right: marginX },
        styles: {
          fontSize: 5,
          cellPadding: 3,
          overflow: 'linebreak',
          valign: 'middle',
        },
        headStyles: {
          fillColor: [46, 111, 159],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 5,
        },
        footStyles: {
          fillColor: [240, 248, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        didParseCell: (data) => {
          if (data.section === 'body' || data.section === 'foot') {
            const col = exportColumns[data.column.index];
            if (col) {
              if (rightAlignedFields.includes(col.field)) {
                data.cell.styles.halign = 'right';
              } else if (centerAlignedFields.includes(col.field)) {
                data.cell.styles.halign = 'center';
              }
            }
          }
        },
        didDrawPage: () => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(
            `Page ${pageCurrent} of ${pageCount}`,
            pageWidth - marginX,
            pageHeight - 10,
            { align: 'right' }
          );
        },
      });

      doc.save(`backend-costs-${new Date().toISOString().split('T')[0]}.pdf`);

      setSuccessMessage('Export completed successfully!');
      setMessageType('success');
      setShowDropdownMenu(false);

    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setSuccessMessage('Error exporting data to PDF');
      setMessageType('error');
      setShowDropdownMenu(false);
    }
  };


  const toggleDropdownMenu = () => {
    setShowDropdownMenu(!showDropdownMenu);
  };

  // Calculate totals row
  const totalsRow = useMemo(() => {
    return {
      id: null,
      jobCostID: null,
      backendCostType: '',
      description: '',
      clientEstimate: displayTotals.clientEstimate,
      totalClientEstCost: displayTotals.totalClientEstCost,
      totalEstCost: displayTotals.totalEstCost,
      totalEstimatedCost: displayTotals.totalEstCost,
      actualCost: displayTotals.totalActualCost,
      costVariance: displayTotals.totalVariance,
      costVariancePercentage: displayTotals.totalVariancePercent,
      estQuantity: displayTotals.totalEstQuantity,
      actualQuantity: displayTotals.totalActualQuantity,
      totalActualCost: displayTotals.totalActualCostDisplay,
      isTotalsRow: true
    };
  }, [displayTotals]);

  const rowDataWithTotals = useMemo(() => {
    const rows = backendData.filter(r => !r.isAddRow && !r.isTotalsRow);

    if (isEditMode) {
      rows.push({
        backendCostType: '+ New Component',
        isAddRow: true
      });
    }

    return rows;
  }, [backendData, isEditMode]);

  const getRowId = useCallback((params) => {
    if (params.data?.isTotalsRow) return 'totals-row';
    if (params.data?.isAddRow) return 'add-row';
    if (params.data?.id) return String(params.data.id);
    if (params.data?.jobCostID) return String(params.data.jobCostID);
    return undefined;
  }, []);
  const columnDefs = useMemo(() => [
    {
      field: 'backendCostType',
      headerName: 'Cost Type',
      width: 150,
      minWidth: 150, pinned: 'left',
      lockPinned: true,

      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isAddRow) {
          return <span style={{ color: '#007bff' }}>+ New Component</span>;
        }

        if (params.data?.isTotalsRow) return <span></span>;

        const isNewRow = params.data?.isNewRow;

        if (isEditMode && isNewRow) {
          return (
            <CostTypeDropdown
              value={params.value}
              id={params.data?.id}
              costTypeOptions={costTypeOptions}
              isEditMode={isEditMode}
              onFieldChange={handleFieldChange}
            />
          );
        }

        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="backendCostType"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      cellStyle: (params) => {
        if (params.data?.isAddRow) {
          return { backgroundColor: '#ffffff', cursor: 'pointer', color: '#007bff', fontWeight: '500' };
        }
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
        }
        if (params.data?.isNewRow && (!params.value || params.value.trim() === '')) {
          return { fontWeight: '500', backgroundColor: '#ffebee' };
        }
        if (params.data?.isNewRow) {
          return { fontWeight: '500', backgroundColor: '#e8f5e9' };
        }
        return { fontWeight: '500', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 200,
      minWidth: 200,
      flex: 1,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) {
          return <span style={{ fontWeight: 'bold' }}>TOTAL</span>;
        }
        if (params.data?.isAddRow) {
          return <span></span>;
        }
        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="description"
            placeholder="Enter description"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff' };
        }
        return null;
      }
    },
    {
      field: 'clientEstimateQuantity',
      headerName: 'Client Est. Inv #',
      width: 200,
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span></span>;
        }
        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="clientEstimateQuantity"
            type="text"
            placeholder="Invoice #"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        }
        return { textAlign: 'right' };
      }
    },
    {
      field: 'clientEstimate',
      headerName: 'Client Estimate',
      width: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span></span>;
        }
        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="clientEstimate"
            type="number"
            isCurrency={true}
            placeholder="0.00"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        }
        return { textAlign: 'right' };
      }
    },
    {
      field: 'clientRateType',
      headerName: 'M/F',
      width: 120,
      minWidth: 120,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span></span>;
        }
        return (
          <MFRateDropdown
            value={params.value}
            id={params.data?.id}
            field="clientRateType"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      filter: 'agTextColumnFilter',
      sortable: false,
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'totalClientEstCost',
      headerName: 'Total Client Est. Cost',
      minWidth: 220,
      filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) {
          return <span style={{ fontWeight: 'bold' }}>${params.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>;
        }
        if (params.data?.isAddRow) {
          return <span></span>;
        }
        return <span>${params.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        }
        return { textAlign: 'right', fontWeight: 'bold', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'poNumber',
      headerName: 'PO Number',
      width: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span>{params.value || ''}</span>;
        }
        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="poNumber"
            placeholder="PO Number"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      }
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      width: 150,
      minWidth: 150,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span>{params.value || ''}</span>;
        }
        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="vendor"
            placeholder="Vendor name"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      }
    },
    {
      field: 'invoiceNumber',
      headerName: 'Invoice Number',
      width: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span>{params.value || ''}</span>;
        }
        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="invoiceNumber"
            placeholder="Invoice #"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      }
    },
    {
      field: 'estQuantity',
      headerName: 'Est. Quantity',
      width: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) {
          return <span style={{ fontWeight: 'bold', textAlign: 'right' }}>
            {params.value?.toLocaleString() || '0'}
          </span>;
        }
        if (params.data?.isAddRow) {
          return <span></span>;
        }
        const displayValue =
          params.value === null || params.value === undefined
            ? '0'
            : params.value.toLocaleString();
        return <span>{displayValue}</span>;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        }
        return { textAlign: 'right' };
      }
    },
    {
      field: 'estCost',
      headerName: 'Est. Cost',
      width: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span></span>;
        }
        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="estCost"
            type="number"
            isCurrency={true}
            placeholder="0.00"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'estimatedRateType',
      headerName: 'M/F',
      width: 120,
      minWidth: 120,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span></span>;
        }
        return (
          <MFRateDropdown
            value={params.value}
            id={params.data?.id}
            field="estimatedRateType"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      filter: 'agTextColumnFilter',
      sortable: false,
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'totalEstimatedCost',
      headerName: 'Total Est. Cost',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) {
          return <span style={{ fontWeight: 'bold' }}>${params.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>;
        }
        if (params.data?.isAddRow) {
          return <span></span>;
        }
        return <span>${params.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        }
        return { textAlign: 'right', fontWeight: 'bold', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'actualQuantity',
      headerName: 'Actual Quantity',
      width: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) {
          return (
            <span style={{ fontWeight: 'bold', textAlign: 'right' }}>
              {params.value?.toLocaleString() || '0'}
            </span>
          );
        }
        if (params.data?.isAddRow) {
          return <span></span>;
        }
        const displayValue =
          params.value === null || params.value === undefined
            ? '0'
            : params.value.toLocaleString();
        return <span>{displayValue}</span>;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        }
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'actualCost',
      headerName: 'Act. Cost',
      width: 150,
      minWidth: 150,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span></span>;
        }
        return (
          <EditableInputRenderer
            value={params.value}
            id={params.data?.id}
            field="actualCost"
            type="number"
            isCurrency={true}
            placeholder="0.00"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'actualRateType',
      headerName: 'M/F',
      width: 150,
      minWidth: 150,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span></span>;
        }
        return (
          <MFRateDropdown
            value={params.value}
            id={params.data?.id}
            field="actualRateType"
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />
        );
      },
      filter: 'agTextColumnFilter',
      sortable: false,
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'totalActualCost',
      headerName: 'Total Act. Cost',
      width: 200,
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) {
          return <span style={{ fontWeight: 'bold', textAlign: 'right' }}>
            ${params.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
          </span>;
        }
        if (params.data?.isAddRow) {
          return <span></span>;
        }
        return <CurrencyRenderer {...params} />;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) {
          return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        }
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      minWidth: 150,
      cellRenderer: (params) => (
        <StatusDropdownCell
          data={params.data}
          isEditMode={isEditMode}
          onFieldChange={handleFieldChange}
        />
      ),
      filter: 'agTextColumnFilter',
      sortable: false
    },
    {
      field: 'actulaCostMFCalc',
      headerName: '/M',
      width: 100,
      minWidth: 100,
      valueFormatter: (params) => formatNumber(params.value),
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) {
          return <span></span>;
        }
        const displayValue = (params.value === null || params.value === undefined) ? 0 : params.value;
        return <span>{formatNumber(displayValue)}</span>;
      },
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'costVariance',
      headerName: 'Cost Variance',
      width: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: VarianceRenderer,
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'costVariancePercentage',
      headerName: 'Cost Variance %',
      width: 350,
      minWidth: 350,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: VariancePercentRenderer,
      cellStyle: { textAlign: 'right' }
    }
  ], [isEditMode, handleFieldChange]);

  if (loading) {
    return <AisScreenLoader />;
  }

  return (
    <>
      <div className='main-container-backend-costs-details p-4'>
        <div className="backend-costs-container">
          {/* Breadcrumb Navigation */}
          <div className="job-details-breadcrumb px-0 pb-4">
            <nav className="breadcrumb-nav">


              <ol className="breadcrumb-list">


                {showReportsCrumb && (
                  <>
                    <li className="breadcrumb-item">
                      <a
                        href="/reports"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/reports', {
                            state: {
                              restoreReports: true,
                              reportFilters: reportsContext.reportFilters || {},
                              selectedClientId: reportsContext.selectedClientId || [],
                              reportType: reportsContext.reportType || '',
                              rowData: reportsContext.rowData || []
                            }
                          });
                        }}
                      >
                        Reports
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {/* ✅ Client Select */}
                {showClientSelectCrumb && (
                  <>
                    <li className="breadcrumb-item">
                      <Icon
                        name="user-icon"
                        size={14}
                        alt="user-icon"
                        className="breadcrumb-icon"
                      />
                      <a
                        href="/dashboard"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          sessionStorage.removeItem('jobDetailsSource');
                          sessionStorage.removeItem('isAllClientsFlow');
                          navigate('/dashboard');
                        }}
                      >
                        Client Select
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {/* ✅ Client */}
                {showClientNameCrumb && (
                  <>
                    <li className="breadcrumb-item">
                      <a
                        href="/client-dashboard"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/client-dashboard', {
                            state: {
                              clientId: clientId,
                              source: 'client-dashboard',
                              isAllClients: false
                            }
                          });
                        }}
                      >
                        Client: {clientName}
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {/* Integration crumb — client flow (Tallies -> LoL -> Job -> Cost -> Package) */}
                {cameFromIntegration && navigationSource === 'client-dashboard' && (
                  <>
                    <li className="breadcrumb-item">
                      <a
                        href="#"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/talliesSection', {
                            state: {
                              activeTab: 'lists',
                              clientId: clientId,
                              clientName: clientName
                            }
                          });
                        }}
                      >
                        Integration
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {navigationSource === 'list-upload' && (
                  <>
                    <li className="breadcrumb-item">
                      <a
                        href="#"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          sessionStorage.removeItem('jobDetailsSource');
                          sessionStorage.removeItem('isAllClientsFlow');
                          navigate('/talliesSection', {
                            state: {
                              activeTab: 'lists'
                            }
                          });
                        }}
                      >
                        Integration
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {/* ✅ Job Lists (ONLY when coming from module) */}
                {navigationSource === 'job-module' && !showClientSelectCrumb && (
                  <>
                    <li className="breadcrumb-item d-flex align-items-center">
                      <span style={{ paddingRight: "4px" }}>
                        <Icon name="mail-icon" size={14} />
                      </span>
                      <a
                        href="/job-module"
                        className="breadcrumb-link"
                        onClick={handleJobListsClick}
                      >
                        Job Lists
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}



                {/* ✅ Job Details */}
                <li className="breadcrumb-item">
                  <a
                    href={`/job-details/${activeJobId}`}
                    className="breadcrumb-link"
                    onClick={handleJobDetailsClick}
                  >
                    Job Details: {jobCode}
                  </a>
                </li>

                <li className="breadcrumb-separator">/</li>

                {/* ✅ Cost Type Dropdown */}
                <li className="breadcrumb-item dropdown-container" style={{ position: 'relative' }}>
                  <div
                    className="breadcrumb-dropdown-trigger"
                    onClick={() => setShowCostTypeDropdown(!showCostTypeDropdown)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: showCostTypeDropdown ? '#e9ecef' : 'transparent'
                    }}
                  >
                    <span className="active">
                      {selectedCostType === 'postage' && 'Postage'}
                      {selectedCostType === 'package' && 'Package Costs'}
                      {selectedCostType === 'backend' && 'Backend Costs'}
                      {selectedCostType === 'lists' && 'Lists'}
                    </span>
                    <Icon name="expand-open" size={16} />
                  </div>

                  {showCostTypeDropdown && (
                    <div
                      className="cost-type-dropdown-menu"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '150px'
                      }}
                    >
                      <div onClick={() => handleCostTypeChange('postage')} className="dropdown-option" style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedCostType === 'postage' ? '#f0f8ff' : 'white',
                        borderBottom: '1px solid #eee'
                      }}>Postage</div>
                      <div onClick={() => handleCostTypeChange('package')} className="dropdown-option" style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedCostType === 'package' ? '#f0f8ff' : 'white',
                        borderBottom: '1px solid #eee'
                      }}>Package Costs</div>
                      <div onClick={() => handleCostTypeChange('backend')} className="dropdown-option" style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedCostType === 'backend' ? '#f0f8ff' : 'white'
                      }}>Backend Costs</div>
                      <div onClick={() => handleCostTypeChange('lists')} className="dropdown-option" style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedCostType === 'lists' ? '#f0f8ff' : 'white'
                      }}>Lists</div>
                    </div>
                  )}
                </li>

              </ol>

            </nav>
          </div>

          <h1 className="job-details-title pb-4">
            {jobCode} Backend Costs
          </h1>

          <div className="backend-costs-grid-container">
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div className="left-side-buttons">
                {!isEditMode && (
                  <button
                    className='manage-components-button'
                    onClick={handleManageComponents}
                  >
                    Manage Components
                  </button>
                )}
              </div>

              <div className="job-details-menu-container" ref={menuDropdownRef}>
                {isEditMode ? (
                  <div className="action-buttons d-flex gap-2">
                    <button
                      className="delete-btn btn btn-danger btn-sm"
                      onClick={handleDeleteClick}
                    >
                      Delete
                    </button>
                    <button
                      className="cancel-delete-btn"
                      onClick={handleCancelChanges}
                    >
                      Cancel
                    </button>
                    <button
                      className="save-changes-btn"
                      onClick={handleSaveChanges}
                      disabled={savingChanges}
                    >
                      {savingChanges ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <button
                      className="job-details-menu-buttons"
                      onClick={toggleDropdownMenu}
                    >
                      <span className="menu-dot" ></span>
                      <span className="menu-dot" ></span>
                      <span className="menu-dot"></span>
                    </button>

                    {showDropdownMenu && (
                      <div className="dropdown-menu-container" style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '160px',
                        overflow: 'hidden'
                      }}>
                        {/* Export to Excel */}
                        <button
                          className="dropdown-menu-item"
                          onClick={() => {
                            exportToExcel();
                            setShowDropdownMenu(false);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="8" y1="13" x2="16" y2="13" />
                            <line x1="8" y1="17" x2="16" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                          </svg>
                          Export to Excel
                        </button>

                        {/* Print To Pdf */}
                        <button
                          className="dropdown-menu-item"
                          onClick={() => {
                            printToPDF();
                            setShowDropdownMenu(false);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9" />
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            <rect x="6" y="14" width="12" height="8" />
                          </svg>
                          Print To PDF
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <AgGrid
              ref={agGridRef}
              rowData={rowDataWithTotals}
              pinnedBottomRowData={[totalsRow]}
              onCellClicked={handleCellClicked}
              columnDefs={columnDefs}
              onGridReady={(params) => {
                setGridApi(params.api);
                gridApiRef.current = params.api;
                const allData = backendData.filter(item => !item.isTotalsRow && !item.isAddRow);
                const initialTotals = calculateTotalsFromData(allData);
                setDisplayTotals(initialTotals);
              }}
              onFilterChanged={handleFilterChanged}
              hideExportButton={true}
              hideSideBar={true}
              hideSelectAll={!isEditMode}
              height="550px"
              enableSorting={true}
              enableFilter={true}
              searchPlaceholder="Search backend costs..."
              noRowsMessage="No backend cost records found"
              noFilteredRowsMessage="No matching backend cost records found"
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                editable: false,
              }}
              gridOptions={{
                // CRITICAL: stable row IDs prevent AG Grid from re-rendering
                // every row on each state update -> no more scroll jumps
                getRowId: getRowId,
                getRowStyle: (params) => {
                  if (params.data?.isTotalsRow) {
                    return { backgroundColor: '#f0f8ff', fontWeight: 'bold' };
                  }
                  if (params.data?.isAddRow) {
                    return { backgroundColor: '#ffffff', cursor: 'pointer' };
                  }
                  if (isEditMode && !params.data?.isTotalsRow && !params.data?.isAddRow) {
                    return { backgroundColor: '#fffef7' };
                  }
                  if (params.data?.isNewRow) {
                    return { backgroundColor: '#e8f5e9' };
                  }
                  return null;
                },
                rowSelection: isEditMode ? 'multiple' : undefined,
                suppressRowClickSelection: true,
                isRowSelectable: (params) => {
                  return !params.data?.isTotalsRow && !params.data?.isAddRow;
                }
              }}
            />
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="delete-confirmation-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div className="delete-confirmation-modal" style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%'
            }}>
              <h3 style={{ marginBottom: '15px' }}>Confirm Delete</h3>
              <p style={{ marginBottom: '20px' }}>Are you sure you want to delete {selectedCostsToDelete.length} item(s)?</p>
              <div className="delete-confirmation-buttons" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}>
                <button
                  className="cancel-delete-btn"
                  onClick={handleCancelDelete}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f0f0f0',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={deleteInProgress}
                >
                  Cancel
                </button>
                <button
                  className="confirm-delete-btn"
                  onClick={handleConfirmDelete}
                  disabled={deleteInProgress}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: deleteInProgress ? 'not-allowed' : 'pointer'
                  }}
                >
                  {deleteInProgress ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>



      {successMessage && (
        <SuccessMessage
          message={successMessage}
          type={messageType}   // ✅ IMPORTANT
          onClose={() => {
            setSuccessMessage('');
            setMessageType('success');
          }}
        />
      )}

    </>
  );
};

export default BackendCostsDetails;
